import { useEffect, useRef, useState } from "react";
import { Calendar, Check, Pencil } from "lucide-react";
import type { UserCardProfile } from "../core/types/tsx/components/UserCard.types";
import { useAuthSessionContext } from "./routing/auth-session-store";
import { useUpdateUser } from "../hooks/control";
import { getImageUri } from "../core/utils/imageLoad";
import defaultAvatarUrl from "../assets/default-avatar.svg";

const fallbackAvatar = defaultAvatarUrl;

const fallbackProfile: UserCardProfile = {
	name: "Usuario",
	email: "-",
	since: "-",
	avatar: fallbackAvatar,
};

function resolveAvatarUrl(raw?: string): string {
	if (!raw) return fallbackAvatar;
	if (raw.length === 1) return raw;
	if (raw.startsWith("blob:")) return raw;
	return getImageUri(raw) ?? raw;
}

export const UserCard = ({
	profile = fallbackProfile,
}: {
	profile?: UserCardProfile;
}) => {
	const { session } = useAuthSessionContext();
	const userId = Number(session?.user?.id ?? 0);
	const { update, loading: updatingAvatar } = useUpdateUser(userId);
	const [avatarSrc, setAvatarSrc] = useState<string>(resolveAvatarUrl(profile.avatar));
	const [uploadState, setUploadState] = useState<
		"idle" | "uploading" | "success" | "success-fade"
	>("idle");
	const [uploadProgress, setUploadProgress] = useState(0);
	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const previewUrlRef = useRef<string | null>(null);
	const progressIntervalRef = useRef<number | null>(null);
	const successFadeTimeoutRef = useRef<number | null>(null);
	const successClearTimeoutRef = useRef<number | null>(null);
	const progressRadius = 14;
	const progressCircumference = 2 * Math.PI * progressRadius;
	const progressOffset =
		progressCircumference - (uploadProgress / 100) * progressCircumference;

	useEffect(() => {
		const nextAvatar = resolveAvatarUrl(profile.avatar);
		if (!previewUrlRef.current) {
			setAvatarSrc(nextAvatar);
		}
	}, [profile.avatar]);

	useEffect(() => {
		return () => {
			if (previewUrlRef.current) {
				URL.revokeObjectURL(previewUrlRef.current);
			}
			if (progressIntervalRef.current) {
				window.clearInterval(progressIntervalRef.current);
			}
			if (successFadeTimeoutRef.current) {
				window.clearTimeout(successFadeTimeoutRef.current);
			}
			if (successClearTimeoutRef.current) {
				window.clearTimeout(successClearTimeoutRef.current);
			}
		};
	}, []);

	const onPickImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file || !userId) return;

		const isImage = file.type.startsWith("image/");
		if (!isImage) {
			window.alert("Selecciona un archivo de imagen valido.");
			return;
		}

		if (file.size > 5 * 1024 * 1024) {
			window.alert("La imagen no puede superar los 5MB.");
			return;
		}

		if (previewUrlRef.current) {
			URL.revokeObjectURL(previewUrlRef.current);
		}

		const tempPreview = URL.createObjectURL(file);
		previewUrlRef.current = tempPreview;
		setAvatarSrc(tempPreview);

		if (progressIntervalRef.current) {
			window.clearInterval(progressIntervalRef.current);
			progressIntervalRef.current = null;
		}
		if (successFadeTimeoutRef.current) {
			window.clearTimeout(successFadeTimeoutRef.current);
			successFadeTimeoutRef.current = null;
		}
		if (successClearTimeoutRef.current) {
			window.clearTimeout(successClearTimeoutRef.current);
			successClearTimeoutRef.current = null;
		}
		setUploadState("uploading");
		setUploadProgress(0);
		progressIntervalRef.current = window.setInterval(() => {
			setUploadProgress((prev) => (prev >= 90 ? 90 : prev + 5));
		}, 120);

		try {
			const updated = await update({}, { image_profile: file });
			const persistedRaw =
				typeof updated?.image_profile === "string" ? updated.image_profile : null;
			const persistedUrl = resolveAvatarUrl(persistedRaw ?? undefined);

			if (persistedRaw) {
				URL.revokeObjectURL(tempPreview);
				previewUrlRef.current = null;
				setAvatarSrc(persistedUrl);
			}

			if (progressIntervalRef.current) {
				window.clearInterval(progressIntervalRef.current);
				progressIntervalRef.current = null;
			}
			setUploadProgress(100);
			setUploadState("success");
			successFadeTimeoutRef.current = window.setTimeout(() => {
				setUploadState("success-fade");
				successFadeTimeoutRef.current = null;
			}, 700);
			successClearTimeoutRef.current = window.setTimeout(() => {
				setUploadState("idle");
				setUploadProgress(0);
				successClearTimeoutRef.current = null;
			}, 1050);
		} catch {
			if (progressIntervalRef.current) {
				window.clearInterval(progressIntervalRef.current);
				progressIntervalRef.current = null;
			}
			if (successFadeTimeoutRef.current) {
				window.clearTimeout(successFadeTimeoutRef.current);
				successFadeTimeoutRef.current = null;
			}
			if (successClearTimeoutRef.current) {
				window.clearTimeout(successClearTimeoutRef.current);
				successClearTimeoutRef.current = null;
			}
			URL.revokeObjectURL(tempPreview);
			previewUrlRef.current = null;
			setAvatarSrc(resolveAvatarUrl(profile.avatar));
			setUploadState("idle");
			setUploadProgress(0);
			window.alert("No se pudo actualizar la foto de perfil.");
		} finally {
			if (event.target) {
				event.target.value = "";
			}
		}
	};

	const showLetterAvatar = avatarSrc.length === 1;

	return (
		<div className='mb-4 mt-6 px-4 sm:mt-8 sm:px-6 lg:px-8'>
			<div className='flex w-full flex-col items-center gap-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:flex-row sm:gap-6 sm:p-6'>
				<div className='flex flex-col items-center'>
					<div className='relative group h-20 w-20 shrink-0 overflow-hidden rounded-full sm:h-24 sm:w-24'>
						<input
							ref={fileInputRef}
							type='file'
							accept='image/*'
							onChange={onPickImage}
							className='hidden'
							aria-label='Cambiar foto de perfil'
						/>

						{/* Imagen: Ajustamos tamaño fijo y object-cover para evitar deformaciones */}
						{showLetterAvatar ? (
							<div className='h-full w-full rounded-full bg-gray-200 flex items-center justify-center text-3xl sm:text-4xl font-bold text-gray-500 shadow-sm'>
								{avatarSrc}
							</div>
						) : (
							<img
								src={avatarSrc || fallbackAvatar}
								alt='Foto de perfil'
								className='h-full w-full rounded-full object-cover shadow-sm'
							/>
						)}

						{uploadState !== "idle" ? (
							<div className='pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-full bg-black/45'>
								{uploadState === "uploading" ? (
									<div className='relative h-10 w-10'>
										<svg
											viewBox='0 0 36 36'
											className='h-10 w-10 -rotate-90'
										>
											<circle
												cx='18'
												cy='18'
												r={progressRadius}
												stroke='rgba(255,255,255,0.35)'
												strokeWidth='3'
												fill='none'
											/>
											<circle
												cx='18'
												cy='18'
												r={progressRadius}
												stroke='white'
												strokeWidth='3'
												strokeLinecap='round'
												fill='none'
												strokeDasharray={progressCircumference}
												strokeDashoffset={progressOffset}
												className='transition-all duration-150'
											/>
										</svg>
										<span className='absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-white'>
											{uploadProgress}%
										</span>
									</div>
								) : (
									<div
										className={`flex h-10 w-10 items-center justify-center rounded-full bg-green-500 text-white transition-opacity duration-300 ${
											uploadState === "success-fade"
												? "opacity-0"
												: "opacity-100"
										}`}
									>
										<Check className='h-5 w-5' />
									</div>
								)}
							</div>
						) : null}

						<button
							type='button'
							onClick={() => fileInputRef.current?.click()}
							disabled={
								updatingAvatar || uploadState === "uploading" || !userId
							}
							className='cursor-pointer absolute inset-0 flex items-center justify-center rounded-full bg-black/0 text-white opacity-0 transition-all group-hover:bg-black/35 group-hover:opacity-100 focus-visible:bg-black/35 focus-visible:opacity-100 disabled:cursor-not-allowed disabled:opacity-0'
							aria-label='Cambiar foto de perfil'
							title='Cambiar foto de perfil'
						>
							<Pencil className='h-5 w-5' />
						</button>
					</div>
				</div>

				<div className='flex w-full min-w-0 flex-col items-center text-center sm:items-start sm:text-left'>
					{/* Nombre: Gris oscuro, tamaño grande */}
					<h2 className='w-full truncate text-lg font-normal text-gray-600 sm:text-2xl'>
						{profile.name}
					</h2>

					{/* Correo: Texto gris más claro y pequeño */}
					<h3 className='mt-1 w-full truncate text-xs font-normal text-gray-400 sm:text-sm'>
						{profile.email}
					</h3>

					{/* Fecha: Alineación flex para el icono y texto pequeño */}
					<div className='mt-3 flex w-full items-center justify-center text-xs font-light text-gray-400 sm:justify-start'>
						<Calendar strokeWidth={1.5} className='w-4 h-4 mr-2' />
						<span className='truncate'>Miembro desde {profile.since}</span>
					</div>
				</div>
			</div>
		</div>
	);
};
